import { ChevronRight, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import type { TabOption } from "@/components/data-table/tab-filters";
import type { Contract } from "@/types/contract";

import { useContracts } from "@/api/contract-query";
import { GenericDataTable } from "@/components/data-table/table";
import { StatusTag } from "@/components/order/status-tag";
import { Button } from "@/components/ui/button";
import { useFilters } from "@/hooks/use-filters";
import { Filters } from "@/types/filters";
import { OrderStatusEnum } from "@/types/order";
import { sortByToState, stateToSortBy } from "@/types/table-sort";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";

const BaseParams = z.object({
	title: z.string().optional(),
	status: z.string().optional(),
	endDate: z.string().optional(),
	startDate: z.string().optional(),
	buyerApprovalStatus: z.string().optional(),

	pageIndex: z.coerce.number().optional(),
	pageSize: z.coerce.number().optional(),
	sortBy: z.string().regex(/^.+\.(asc|desc)$/).optional()
});

export const ContractParams = Filters(BaseParams);

export const Route = createFileRoute("/vendor/contract/")({
	component: RouteComponent,
	validateSearch: ContractParams
});

const columnHelper = createColumnHelper<Contract>();

const columns = [
	columnHelper.display({
		id: "title",
		cell: ({ row }) => (
			<div className="inline-flex items-center gap-2">
				<img src="/3d-approved.png" className="size-16" alt="contract icon" />
				<div>{row.original.title}</div>

			</div>
		)
	}),
	columnHelper.display({
		id: "code",
		cell: ({ row }) => (
			<div>
				{row.original.contractCode}
			</div>
		)
	}),
	columnHelper.display({
		id: "status",

		cell: ({ row }) => (
			<div className="flex  items-end">
				<div className="ml-auto"></div>
				<StatusTag status={row.original.status as OrderStatusEnum} />
				<ChevronRight className=" mr-2 h-4 w-4 " />
			</div>
		)
	})
];

const tabOptions: TabOption[] = [
	{ value: "", label: "All" },
	{ value: OrderStatusEnum.PAID, label: "Paid" },
	{ value: OrderStatusEnum.PENDING, label: "Pending" },
	{ value: OrderStatusEnum.CANCELED, label: "Cancelled" },
	{ value: OrderStatusEnum.EXPIRED, label: "Expired" }
];

function RouteComponent() {
	const { filters, setFilters } = useFilters(Route.id);
	const navigate = useNavigate();
	const { data, isPending, isFetching, refetch } = useContracts(filters);

	const paginationState = {
		...filters,
		pageIndex: filters.pageIndex,
		pageSize: filters.pageSize
	};

	const sortingState = sortByToState(filters.sortBy);

	const [activeTab, setActiveTab] = useState<string>(filters.status ?? tabOptions[0].value);

	const filteredData = useMemo(() => {
		if (activeTab === tabOptions[0].value)
			return data?.data;
		return data?.data.filter(item => ((item.status).toLowerCase()).includes(activeTab.toLowerCase()));
	}, [activeTab, data]) ?? [];

	useEffect(() => {
		refetch();
	}, [filters, refetch]);

	return (
		<div className="container mx-auto flex flex-col space-y-4">
			<Button asChild>
				<Link className="ml-auto mt-4" to="/vendor/contract/create">
					<Plus className="h-4 w-6" />
					Create new contract
				</Link>
			</Button>
			<GenericDataTable
				columns={columns}
				data={filteredData}
				activeTab={activeTab}
				isLoading={isPending || isFetching}
				tabOptions={tabOptions}
				pagination={paginationState}
				onTabChange={setActiveTab}
				filterField="status"
				onSearchChange={s => setFilters({ ...filters, title: s })}
				paginationOptions={{
					onPaginationChange: (pagination) => {
						setFilters(
							typeof pagination === "function"
								? pagination(paginationState)
								: pagination
						);
					},
					rowCount: data?.totalItems
				}}
				filters={filters}
				handleRowClick={(row) => {
					navigate({
						from: "/vendor/contract",
						to: "/vendor/contract/$contractid",
						params: { contractid: row.original.id }
					});
				}}
				onFilterChange={filters => setFilters({ ...filters })}
				sorting={sortingState}
				onSortingChange={(updaterOrValue) => {
					const newSortingState = typeof updaterOrValue === "function"
						? updaterOrValue(sortingState)
						: updaterOrValue;
					return setFilters({ sortBy: stateToSortBy(newSortingState) });
				}}
			/>
		</div>
	);
}
